/**
 * 관리자 API 클라이언트 — `/api/admin/*`.
 *
 * 공개 조회용 `lib/api.ts` 와 분리한 이유:
 *   - 모든 요청에 `Authorization: Bearer` 를 붙여야 한다.
 *   - 오류 본문이 `{ message }` 규약이라 서버 메시지를 그대로 화면에 보여줄 수 있다.
 *   - 401 은 "로그인 화면으로" 라는 공통 처리가 필요하다.
 *
 * 토큰은 localStorage 에 둔다. 쿠키가 아니라 헤더로 보내므로 CSRF 표면이 없고,
 * dev(:5173)와 prod(동일 origin) 양쪽에서 같은 방식으로 동작한다.
 */

import type { AdminSession } from "../types/admin";

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
const SESSION_KEY = "bridgechecker.admin.session";

export class AdminApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "AdminApiError";
    this.status = status;
  }
}

// ─── 세션 보관 ────────────────────────────────────────────────────────────────

/**
 * 저장된 세션을 읽는다. 만료됐거나 형식이 깨졌으면 지우고 null.
 * (만료 토큰으로 요청해 401 을 받는 것보다, 먼저 로그인 화면을 보여주는 편이 덜 혼란스럽다.)
 */
export function loadSession(): AdminSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AdminSession;
    if (!parsed?.token || !parsed?.expiresAt) {
      clearSession();
      return null;
    }
    if (new Date(parsed.expiresAt).getTime() <= Date.now()) {
      clearSession();
      return null;
    }
    return parsed;
  } catch {
    clearSession();
    return null;
  }
}

export function saveSession(session: AdminSession): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function clearSession(): void {
  localStorage.removeItem(SESSION_KEY);
}

/**
 * 401 을 받았을 때 호출되는 훅. `AdminPage` 가 마운트되면서 등록해
 * 어느 요청에서 401 이 나든 한 곳에서 로그인 화면으로 되돌린다.
 */
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

// ─── 요청 ─────────────────────────────────────────────────────────────────────

/** 오류 본문에서 `message` 를 꺼낸다. JSON 이 아니면 상태 코드 기반 기본 문구. */
async function errorMessage(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { message?: string };
    if (body?.message) return body.message;
  } catch {
    // 본문이 비었거나 JSON 이 아님 — 아래 기본 문구로.
  }
  if (res.status === 401) return "로그인이 필요합니다.";
  if (res.status === 403) return "권한이 없습니다.";
  if (res.status === 503) return "서버에 관리자 계정이 설정되지 않았습니다.";
  return `요청이 실패했습니다 (${res.status}).`;
}

async function request<T>(
  method: string,
  path: string,
  options: { token?: string | null; body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (options.token) headers.Authorization = `Bearer ${options.token}`;
  if (options.body !== undefined) headers["Content-Type"] = "application/json";

  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  });

  if (!res.ok) {
    const message = await errorMessage(res);
    // 로그인 요청 자체의 401(비밀번호 오류)은 세션을 건드릴 게 없으므로 제외한다.
    if (res.status === 401 && !path.endsWith("/login")) {
      clearSession();
      onUnauthorized?.();
    }
    throw new AdminApiError(message, res.status);
  }

  // 204 등 본문 없는 응답 방어 — 서버는 항상 JSON 을 주지만 형식을 강제하지 않는다.
  const text = await res.text();
  return (text ? JSON.parse(text) : null) as T;
}

export const adminApi = {
  get: <T>(path: string, token: string | null, signal?: AbortSignal) =>
    request<T>("GET", path, { token, signal }),
  post: <T>(path: string, token: string | null, body?: unknown) =>
    request<T>("POST", path, { token, body: body ?? {} }),
  put: <T>(path: string, token: string | null, body: unknown) =>
    request<T>("PUT", path, { token, body }),
  del: <T>(path: string, token: string | null) =>
    request<T>("DELETE", path, { token }),
};
